import React, { useState, useMemo } from 'react';
import { Period, PeriodPrediction, PeriodStats } from '../../services/periodCalendarService';
import './PeriodCalendarView.css';

interface PeriodCalendarViewProps {
  periods: Period[];
  predictions?: PeriodPrediction[];
  stats?: PeriodStats | null;
  onDateClick?: (date: string) => void;
}

interface PredictedPeriod {
  start_date: string;
  end_date: string;
}

const PeriodCalendarView: React.FC<PeriodCalendarViewProps> = ({ periods, predictions = [], stats, onDateClick }) => {
  const [currentMonth, setCurrentMonth] = useState(new Date());

  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 
    'July', 'August', 'September', 'October', 'November', 'December'];
  
  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  const getDaysInMonth = (date: Date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (date: Date) => {
    return new Date(date.getFullYear(), date.getMonth(), 1).getDay();
  };

  const getPredictedPeriods = useMemo((): PredictedPeriod[] => {
    if (!predictions || predictions.length === 0) {
      return [];
    }

    const predictedPeriods: PredictedPeriod[] = [];
    // Use average duration from stats, or default to 5 days if not available
    const avgDuration = (stats && stats.average_period_duration) ? stats.average_period_duration : 5;

    predictions.forEach(prediction => {
      const startDateStr = prediction.predicted_start_date;
      // Parse date in local timezone to avoid timezone issues
      const startDate = new Date(startDateStr);
      startDate.setHours(0, 0, 0, 0);
      const endDate = new Date(startDate);
      endDate.setDate(endDate.getDate() + avgDuration - 1); // -1 because start date is included
      
      // Format dates as YYYY-MM-DD
      const endDateStr = endDate.getFullYear() + '-' + 
        String(endDate.getMonth() + 1).padStart(2, '0') + '-' + 
        String(endDate.getDate()).padStart(2, '0');

      predictedPeriods.push({
        start_date: startDateStr,
        end_date: endDateStr
      });
    });

    return predictedPeriods;
  }, [predictions, stats]);

  const isDateInPeriod = (date: Date): { period: Period | null; isPredicted: boolean } => {
    const dateStr = formatDateKey(date);
    
    // Check actual periods first
    for (const period of periods) {
      const periodStart = normalizeDateString(period.start_date);
      const periodEnd = normalizeDateString(period.end_date || period.start_date);
      
      if (dateStr >= periodStart && dateStr <= periodEnd) {
        return { period, isPredicted: false };
      }
    }
    
    // Check predicted periods
    for (const predictedPeriod of getPredictedPeriods) {
      // Check if this date falls within the predicted period range
      if (dateStr >= predictedPeriod.start_date && dateStr <= predictedPeriod.end_date) {
        return { period: null, isPredicted: true };
      }
    }
    
    return { period: null, isPredicted: false };
  };

  const calendarDays = useMemo(() => {
    const days: (Date | null)[] = [];
    const firstDay = getFirstDayOfMonth(currentMonth);
    const daysInMonth = getDaysInMonth(currentMonth);
    
    for (let i = 0; i < firstDay; i++) {
      days.push(null);
    }
    
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day));
    }
    
    return days;
  }, [currentMonth]);

  const navigateMonth = (direction: 'prev' | 'next') => {
    setCurrentMonth(prev => {
      const newDate = new Date(prev);
      if (direction === 'prev') {
        newDate.setMonth(prev.getMonth() - 1);
      } else {
        newDate.setMonth(prev.getMonth() + 1);
      }
      return newDate;
    });
  };

  const goToToday = () => {
    setCurrentMonth(new Date());
  };

  const formatDateKey = (date: Date): string => {
    // Format as YYYY-MM-DD in local timezone to avoid timezone issues
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const normalizeDateString = (dateStr: string): string => {
    // Extract YYYY-MM-DD from ISO string (e.g., "2025-10-20T17:00:00.000Z" -> "2025-10-20")
    // or return as-is if already in YYYY-MM-DD format
    if (dateStr.includes('T')) {
      return dateStr.split('T')[0];
    }
    return dateStr;
  };

  return (
    <div className="period-calendar-view">
      <div className="period-calendar-view-header">
        <button 
          className="period-calendar-view-nav-btn"
          onClick={() => navigateMonth('prev')}
          aria-label="Previous month"
        >
          ‹
        </button>
        <div className="period-calendar-view-month-year">
          <span className="period-calendar-view-month">{monthNames[currentMonth.getMonth()]}</span>
          <span className="period-calendar-view-year">{currentMonth.getFullYear()}</span>
        </div>
        <button 
          className="period-calendar-view-nav-btn"
          onClick={() => navigateMonth('next')}
          aria-label="Next month"
        >
          ›
        </button>
        <button 
          className="period-calendar-view-today-btn"
          onClick={goToToday}
        >
          Today
        </button>
      </div>

      <div className="period-calendar-view-grid">
        <div className="period-calendar-view-weekdays">
          {dayNames.map(day => (
            <div key={day} className="period-calendar-view-weekday">
              {day}
            </div>
          ))}
        </div>

        <div className="period-calendar-view-days">
          {calendarDays.map((date, index) => {
            if (!date) {
              return <div key={`empty-${index}`} className="period-calendar-view-day empty" />;
            }

            const { period, isPredicted } = isDateInPeriod(date);
            const isToday = formatDateKey(date) === formatDateKey(new Date());
            const isStartDate = period && formatDateKey(date) === normalizeDateString(period.start_date);
            const isEndDate = period && period.end_date && formatDateKey(date) === normalizeDateString(period.end_date);
            
            // Check if it's a predicted start/end date
            let isPredictedStart = false;
            let isPredictedEnd = false;
            if (isPredicted) {
              for (const predictedPeriod of getPredictedPeriods) {
                const dateStr = formatDateKey(date);
                if (dateStr === predictedPeriod.start_date) {
                  isPredictedStart = true;
                }
                if (dateStr === predictedPeriod.end_date) {
                  isPredictedEnd = true;
                }
              }
            }

            const dateStr = formatDateKey(date);
            let title = '';
            if (period) {
              const periodStart = normalizeDateString(period.start_date);
              const periodEnd = period.end_date ? normalizeDateString(period.end_date) : null;
              title = `Period: ${periodStart}${periodEnd ? ` - ${periodEnd}` : ''}`;
            } else if (isPredicted) {
              const predictedPeriod = getPredictedPeriods.find(p => 
                dateStr >= p.start_date && dateStr <= p.end_date
              );
              if (predictedPeriod) {
                title = `Predicted Period: ${predictedPeriod.start_date} - ${predictedPeriod.end_date}`;
              }
            }

            return (
              <div
                key={dateStr}
                className={`period-calendar-view-day ${period ? 'has-period' : ''} ${isPredicted ? 'has-predicted-period' : ''} ${isToday ? 'today' : ''} ${isStartDate ? 'start-date' : ''} ${isEndDate ? 'end-date' : ''} ${isPredictedStart ? 'predicted-start-date' : ''} ${isPredictedEnd ? 'predicted-end-date' : ''}`}
                onClick={() => onDateClick && onDateClick(dateStr)}
                title={title}
              >
                <span className="period-calendar-view-day-number">{date.getDate()}</span>
                {(period || isPredicted) && (
                  <div className={`period-calendar-view-period-indicator ${isPredicted ? 'predicted' : ''}`} />
                )}
              </div>
            );
          })}
        </div>
      </div>

      <div className="period-calendar-view-legend">
        <div className="period-calendar-view-legend-item">
          <div className="period-calendar-view-legend-color has-period"></div>
          <span>Period days</span>
        </div>
        {getPredictedPeriods.length > 0 && (
          <div className="period-calendar-view-legend-item">
            <div className="period-calendar-view-legend-color has-predicted-period"></div>
            <span>Predicted period</span>
          </div>
        )}
        <div className="period-calendar-view-legend-item">
          <div className="period-calendar-view-legend-color today"></div>
          <span>Today</span>
        </div>
      </div>
    </div>
  );
};

export default PeriodCalendarView;

